import { useEffect, useState } from 'react'
import '../styles/RegionPopup.css'
import { countryLabel } from '../utils/countryLabel'
import { getCountryFlag } from '../utils/flagCache'
import { getSeason } from '../utils/season'
import { canRequestAnnexation, canOpenCountryChannel } from '../utils/annexationData'
import { annexationStatusLabel, openCountryChannel } from '../utils/annexation'
import { DASHBOARD_PUBLIC_URL } from '../api/client'

const RegionPopup = ({ region, onClose, onCenterMap, onShareLocation, stats, weather, currentMonth, profile, annexation, hasOpenAnnexation, onRequestAnnexation }) => {
  const [flagUrl, setFlagUrl] = useState(null)
  const [annexationOpen, setAnnexationOpen] = useState(false)
  const [annexationReason, setAnnexationReason] = useState('')
  const [annexationPending, setAnnexationPending] = useState(false)
  const [channel, setChannel] = useState(null)
  const [channelError, setChannelError] = useState(null)
  const [channelPending, setChannelPending] = useState(false)

  useEffect(() => {
    getCountryFlag(region.country_id, setFlagUrl)
    setAnnexationOpen(false)
    setAnnexationReason('')
    setChannel(null)
    setChannelError(null)
  }, [region.country_id, region.region_id])

  const annexationTarget = profile?.country_id
    && Number(region.country_id || 0) !== Number(profile.country_id)
    && (!region.country_id || region.is_played === false)
  const annexationAllowed = canRequestAnnexation(region, profile, hasOpenAnnexation)

  // À qui peut-on écrire : un pays TIERS, effectivement joué, quand on siège
  // soi-même dans un gouvernement. Un canal avec son propre pays n'a pas de
  // sens et le serveur le refuse — proposer le bouton produirait une erreur
  // que le joueur lirait comme une panne.
  const negotiateTarget = canOpenCountryChannel(region, profile)

  const startChannel = async () => {
    if (!negotiateTarget || channelPending) return
    setChannelPending(true)
    setChannelError(null)
    try {
      const data = await openCountryChannel(Number(region.country_id))
      setChannel(data)
    } catch (error) {
      // L'erreur est montrée telle quelle : « vous ne siégez pas au
      // gouvernement de ce pays » est une information utile, et la masquer
      // derrière « une erreur est survenue » laisserait le joueur réessayer.
      setChannelError(error?.message || 'Impossible d’ouvrir le canal.')
    } finally {
      setChannelPending(false)
    }
  }

  const sendAnnexation = async () => {
    if (!annexationReason.trim() || !annexationAllowed || annexationPending) return
    setAnnexationPending(true)
    try {
      await onRequestAnnexation(region, annexationReason.trim())
      setAnnexationOpen(false)
      setAnnexationReason('')
    } finally {
      setAnnexationPending(false)
    }
  }

  const formatPopulation = (pop) => {
    if (!pop || pop === 0) return 'Non renseigné'
    return pop.toLocaleString('fr-FR')
  }

  const formatArea = (areaKm2) => {
    if (!areaKm2 || areaKm2 === 0) return 'Non renseigné'
    if (areaKm2 >= 1000) {
      return `${areaKm2.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} km²`
    }
    return `${areaKm2.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km²`
  }

  const fmt = (n) => (n ?? 0).toLocaleString('fr-FR')

  // Owned-country data: only present for the logged-in player's own regions.
  // Build a mini table: rows = type (+ specialisation), columns = levels present.
  const buildStructTable = (detail) => {
    const rows = new Map() // label -> { byLevel: {lvl: n}, total }
    const levels = new Set()
    ;(detail || []).forEach((d) => {
      if (!d.count) return
      levels.add(Number(d.level))
      const label = `${d.type}${d.specialisation && d.specialisation !== 'NA' ? ' ' + d.specialisation : ''}`
      const row = rows.get(label) || { byLevel: {}, total: 0 }
      row.byLevel[d.level] = (row.byLevel[d.level] || 0) + d.count
      row.total += d.count
      rows.set(label, row)
    })
    return {
      levels: [...levels].sort((a, b) => a - b),
      rows: [...rows.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([label, r]) => ({ label, ...r })),
    }
  }

  const structTable = stats ? buildStructTable(stats.structures_detail) : { levels: [], rows: [] }
  const infraEntries = stats ? Object.entries(stats.infrastructure || {}) : []

  const getHemisphereText = (hemisphere) => {
    if (hemisphere === 'north') return 'Hémisphère Nord'
    if (hemisphere === 'south') return 'Hémisphère Sud'
    if (hemisphere === 'equator') return 'Équateur'
    return 'Non renseigné'
  }

  return (
    <div className="region-popup-overlay" onClick={onClose}>
      <div className="region-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose} aria-label="Fermer">
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="1" y1="1" x2="13" y2="13" />
            <line x1="13" y1="1" x2="1" y2="13" />
          </svg>
        </button>

        <div className="region-header">
          <h2>{region.name}</h2>
          <p className="region-area">{region.geographical_area || region.country_name}</p>
        </div>

        <div className="region-details">
          <div className="detail-row">
            <span className="detail-label">Continent</span>
            <span className="detail-value">{region.continent || 'Non renseigné'}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Population</span>
            <span className="detail-value">{formatPopulation(region.population)}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Surface</span>
            <span className="detail-value">{formatArea(region.area_km2)}</span>
          </div>

          {region.area_km2 > 0 && region.population > 0 && (
            <div className="detail-row">
              <span className="detail-label">Densité</span>
              <span className="detail-value">
                {(region.population / region.area_km2).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} hab/km²
              </span>
            </div>
          )}

          {(region.altitude ?? region.elevation ?? region.avg_altitude) != null && (
            <div className="detail-row">
              <span className="detail-label">Altitude</span>
              <span className="detail-value">
                {fmt(region.altitude ?? region.elevation ?? region.avg_altitude)} m
              </span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Hémisphère</span>
            <span className="detail-value">{getHemisphereText(region.hemisphere)}</span>
          </div>

          {currentMonth && (
            <div className="detail-row">
              <span className="detail-label">Saison</span>
              <span className="detail-value">{getSeason(region.hemisphere, currentMonth)}</span>
            </div>
          )}

          {region.country_name && (
            <div className="detail-row">
              <span className="detail-label">Pays</span>
              <span className="detail-value">
                {flagUrl && <img src={flagUrl} alt="" className="country-flag-icon-sm" />}
                {countryLabel(region.country_name, region.country_id)}
              </span>
            </div>
          )}
          
          {region.country_name && region.is_played !== undefined && (
            <div className="detail-row">
              <span className="detail-label">Statut</span>
              <span className="detail-value">
                {region.is_played ? 'Pays joué' : 'Pays non joué'}
                {region.months_since_activity !== null && region.months_since_activity !== undefined && (
                  <span style={{ marginLeft: '8px', fontSize: '0.9em' }}>
                    ({region.months_since_activity > 5 || region.months_since_activity === null
                      ? '⚠️ Inactif >5 mois'
                      : `Actif (${region.months_since_activity} mois)`})
                  </span>
                )}
              </span>
            </div>
          )}

          {region.climate && (
            <div className="detail-row">
              <span className="detail-label">Climat</span>
              <span className="detail-value">{region.climate}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">Terrain de combat</span>
            <span className="detail-value">{region.combat_terrain || 'Terrain non renseigné'}</span>
          </div>

          {weather && (
            <div className="detail-row">
              <span className="detail-label">Météo</span>
              <span className="detail-value">
                {weather.weather_type}
                {weather.temperature !== undefined && ` — ${Math.round(weather.temperature)}°C`}
                {weather.wind_speed !== undefined && `, vent ${Math.round(weather.wind_speed)} km/h`}
              </span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">ID Région</span>
            <span className="detail-value">#{region.region_id}</span>
          </div>
        </div>

        {stats && (
          <div className="region-owned-stats">
            <h3 className="owned-stats-title">🏙️ Mes données régionales</h3>

            <div className="owned-stats-group">
              <h4>Logement</h4>
              <div className="detail-row">
                <span className="detail-label">Capacité</span>
                <span className="detail-value">{fmt(stats.housing?.capacity)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Couverture</span>
                <span className="detail-value">{fmt(stats.housing?.coverage_pct)} %</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Confort moyen</span>
                <span className="detail-value">{fmt(stats.housing?.average_comfort)}</span>
              </div>
            </div>

            <div className="owned-stats-group">
              <h4>Électricité</h4>
              <div className="detail-row">
                <span className="detail-label">Production</span>
                <span className="detail-value">{fmt(stats.electricity?.production_mwh)} MWh</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Consommation</span>
                <span className="detail-value">{fmt(stats.electricity?.consumption_mwh)} MWh</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Couverture conso.</span>
                <span className="detail-value">{fmt(stats.electricity?.meeting_pct)} %</span>
              </div>
            </div>

            <div className="owned-stats-group">
              <h4>Structures</h4>
              {structTable.rows.length > 0 ? (
                <table className="owned-stats-table">
                  <thead>
                    <tr>
                      <th>Structure</th>
                      {structTable.levels.map((l) => (
                        <th key={l}>Nv.{l}</th>
                      ))}
                      <th>Σ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structTable.rows.map((row) => (
                      <tr key={row.label}>
                        <td className="owned-stats-rowlabel">{row.label}</td>
                        {structTable.levels.map((l) => (
                          <td key={l}>{row.byLevel[l] ? fmt(row.byLevel[l]) : '·'}</td>
                        ))}
                        <td className="owned-stats-total">{fmt(row.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="detail-row">
                  <span className="detail-value owned-stats-empty">Aucune structure</span>
                </div>
              )}
            </div>

            <div className="owned-stats-group">
              <h4>Infrastructures</h4>
              {infraEntries.length > 0 ? (
                infraEntries.map(([type, km]) => (
                  <div className="detail-row" key={type}>
                    <span className="detail-label">{type}</span>
                    <span className="detail-value">{fmt(km)} km</span>
                  </div>
                ))
              ) : (
                <div className="detail-row">
                  <span className="detail-value owned-stats-empty">Aucune infrastructure</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="region-actions">
          {annexation && (
            <div className="annexation-status" role="status">
              <strong>Dossier d’annexion #{annexation.id}</strong>
              <span>{annexationStatusLabel(annexation.status)}</span>
              <small>1 point diplomatique dépensé à l’ouverture</small>
            </div>
          )}
          <button 
            className="center-map-button"
            onClick={() => onCenterMap && onCenterMap(region)}
          >
            📍 Centrer sur la carte
          </button>
          
          <button 
            className="share-location-button"
            onClick={() => onShareLocation && onShareLocation(region)}
          >
            🔗 Copier le lien
          </button>

          {negotiateTarget && !channel && (
            <button
              className="negotiate-button"
              disabled={channelPending}
              onClick={startChannel}
              title="Ouvrir un canal diplomatique avec ce pays"
            >
              {channelPending ? 'Ouverture…' : '✉️ Écrire à ce pays'}
            </button>
          )}

          {channel && (
            <div className="negotiate-status" role="status">
              <strong>Canal diplomatique ouvert</strong>
              <a href={`${DASHBOARD_PUBLIC_URL}/messaging?conversation=${channel.conversation_id}`}
                 target="_blank" rel="noopener noreferrer">
                Ouvrir la conversation
              </a>
            </div>
          )}

          {channelError && (
            <div className="negotiate-error" role="alert">{channelError}</div>
          )}

          {annexationTarget && !annexation && !annexationOpen && (
            <button
              className="annexation-button"
              disabled={!annexationAllowed}
              onClick={() => setAnnexationOpen(true)}
              title={annexationAllowed ? 'Cette ouverture coûte 1 point diplomatique' : (hasOpenAnnexation ? 'Une demande d’annexion est déjà en cours' : 'Un point diplomatique est requis')}
            >
              🤝 Demander l’annexion · 1 point diplomatique
            </button>
          )}

          {annexationOpen && (
            <div className="annexation-form">
              <label htmlFor="annexation-reason">Projet présenté aux autorités locales</label>
              <textarea
                id="annexation-reason"
                rows="4"
                maxLength="2000"
                value={annexationReason}
                onChange={(event) => setAnnexationReason(event.target.value)}
                placeholder="Arguments économiques, culturels ou sécuritaires…"
              />
              <p>Le point ouvre une négociation ; il ne garantit pas l’annexion.</p>
              <div className="annexation-form-actions">
                <button type="button" onClick={() => setAnnexationOpen(false)}>Annuler</button>
                <button
                  type="button"
                  disabled={!annexationReason.trim() || annexationPending}
                  onClick={sendAnnexation}
                >
                  {annexationPending ? 'Envoi…' : 'Dépenser 1 point et envoyer'}
                </button>
              </div>
            </div>
          )}
        </div>

        {region.country_name && (
          <div className="region-footer">
            <span
              className="country-badge"
              style={{ backgroundColor: region.country_color || '#999' }}
            >
              {flagUrl && <img src={flagUrl} alt="" className="country-flag-icon-sm" />}
              {countryLabel(region.country_name, region.country_id)}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default RegionPopup
